import React from 'react';
import './ghosts.css';

// ghosts.jsx — Apiboo ghost marks, wordmarks, icons, and loaders.
// Flat, minimal, Clyde/Discord-style mascot: soft fully-rounded body (smooth
// closed spline), round white eyes (no pupils). Animations: idle (slow float +
// liquid wobble + glance + occasional blink), fly (orbit loader), patrol
// (left/right scan loader), spin (rotate-in-place loader).
// Built on the Booreq visual DNA (dark + #8b5cf6, Inter + JetBrains Mono).
// Exports to window: Ghost, Wordmark, AppIcon, GhostLoader, GhostPatrol,
// GhostSpinner, AP.

const AP = {
  purple: '#8b5cf6',
  purpleSoft: '#a78bfa',
  purpleDeep: '#6d3df2',
  ink: '#0d0c14',
  ink0: '#08070d',
  white: '#f4f3f7'
};

// ── smooth closed spline (Catmull-Rom → cubic Bézier) ──────────────────────
function catmullClosed(pts) {
  const n = pts.length;
  const P = (i) => pts[(i % n + n) % n];
  const f = (v) => v.toFixed(2);
  let d = `M ${f(P(0)[0])} ${f(P(0)[1])} `;
  for (let i = 0; i < n; i++) {
    const p0 = P(i - 1),p1 = P(i),p2 = P(i + 1),p3 = P(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6,c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6,c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C ${f(c1x)} ${f(c1y)}, ${f(c2x)} ${f(c2y)}, ${f(p2[0])} ${f(p2[1])} `;
  }
  return d + 'Z';
}

// ── ghost silhouette points (120×144 viewBox) ──────────────────────────────
function ghostPoints({ lx, rx, topY, sideBottomY, baseY, amp, bumps, ampArr = null }) {
  const cx = (lx + rx) / 2,w = rx - lx,h = sideBottomY - topY;
  const pts = [
  [lx, sideBottomY],
  [lx, topY + h * 0.46],
  [lx + w * 0.13, topY + h * 0.12],
  [cx, topY],
  [rx - w * 0.13, topY + h * 0.12],
  [rx, topY + h * 0.46],
  [rx, sideBottomY]];

  const seg = (rx - lx) / bumps;
  for (let i = 0; i < bumps; i++) {
    const a = (ampArr ? ampArr[i] : 1) * amp;
    pts.push([rx - seg * (i + 0.5), baseY + a]);
    if (i < bumps - 1) pts.push([rx - seg * (i + 1), baseY - amp * 0.12]);
  }
  return pts;
}

const VAR = {
  classic: { body: { lx: 16, rx: 104, topY: 15, sideBottomY: 97, baseY: 99, amp: 11, bumps: 3 }, eye: { r: 10, cy: 55, cxL: 47, cxR: 73 } },
  gradient: { body: { lx: 13, rx: 107, topY: 12, sideBottomY: 100, baseY: 102, amp: 13, bumps: 3 }, eye: { r: 10.5, cy: 55, cxL: 46, cxR: 74 } },
  pop: { body: { lx: 16, rx: 104, topY: 15, sideBottomY: 97, baseY: 99, amp: 11, bumps: 3 }, eye: { r: 10, cy: 55, cxL: 47, cxR: 73 } },
  outline: { body: { lx: 19, rx: 101, topY: 16, sideBottomY: 96, baseY: 98, amp: 9, bumps: 3 }, eye: { r: 8, cy: 55, cxL: 48, cxR: 72 } }
};

// ── liquid morph frame ─────────────────────────────────────────────────────
function morphFrame(b, { sx = 1, sy = 1, shear = 0, amps = null } = {}) {
  const cx = (b.lx + b.rx) / 2;
  const groundY = b.baseY + b.amp;
  const pts = ghostPoints({ ...b, ampArr: amps }).map(([x, y]) => {
    const hAbove = groundY - y;
    return [cx + (x - cx) * sx + shear * (hAbove / groundY), groundY - hAbove * sy];
  });
  return catmullClosed(pts);
}

function bodyMorphValues(b) {
  const f = (o) => morphFrame(b, o);
  return [
  f({}),
  f({ sx: 1.04, sy: 0.95, amps: [1.18, 0.82, 1.14] }),
  f({ sx: 0.97, sy: 1.04, shear: 2.6, amps: [0.85, 1.15, 0.88] }),
  f({ sx: 1.025, sy: 0.98, shear: -2, amps: [1.1, 0.88, 1.05] }),
  f({})].join(';');
}

function RoundEye({ cx, cy, r, fill, stroke = 'none', sw = 0, className }) {
  return (
    <circle className={className} cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={sw}
    style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />);

}

// ── the Ghost mark ────────────────────────────────────────────────────────
// variant: 'classic' | 'gradient' | 'pop' | 'outline'
// mode (when animated): 'idle' | 'fly' | 'patrol' | 'spin'
function Ghost({ variant = 'classic', size = 120, animated = false, mode = 'idle', faceless = false, mono = null, glow = false, style = {} }) {
  const uid = React.useMemo(() => 'g' + Math.random().toString(36).slice(2, 8), []);
  const cfg = VAR[variant] || VAR.classic;
  const isMono = !!mono;

  let bodyFill,eyeFill,eyeStroke = 'none',eyeSW = 0,bodyStroke = 'none',strokeW = 0,defs = null;
  if (isMono) {
    const lightOnDark = mono === '#fff' || mono === AP.white;
    bodyFill = variant === 'outline' ? 'none' : mono;
    bodyStroke = variant === 'outline' ? mono : 'none';
    strokeW = variant === 'outline' ? 5 : 0;
    eyeFill = variant === 'outline' ? mono : lightOnDark ? 'var(--accent-hover)' : '#fff';
  } else if (variant === 'classic') {
    bodyFill = 'var(--accent)';eyeFill = '#fff';
  } else if (variant === 'gradient') {
    bodyFill = `url(#${uid}grad)`;eyeFill = '#fff';
    defs =
    <linearGradient id={`${uid}grad`} x1="0" y1="0" x2="0.25" y2="1">
        <stop offset="0" style={{ stopColor: 'var(--accent-soft)' }} />
        <stop offset="1" style={{ stopColor: 'var(--accent-hover)' }} />
      </linearGradient>;

  } else if (variant === 'pop') {
    bodyFill = '#fff';eyeFill = 'var(--accent-hover)';
  } else if (variant === 'outline') {
    bodyFill = 'none';bodyStroke = 'var(--accent)';strokeW = 5;eyeFill = 'var(--accent)';
  }

  const e = cfg.eye,b = cfg.body;
  const rest = catmullClosed(ghostPoints(b));
  const fly = animated && mode === 'fly';
  const patrol = animated && mode === 'patrol';
  const spin = animated && mode === 'spin';
  const morphVals = animated ? bodyMorphValues(b) : null;
  const morphDur = fly ? '2.4s' : spin ? '2.4s' : patrol ? '3.2s' : '6.5s';
  const SPL = '0.45 0 0.55 1';
  const blinkClass = !animated ? undefined : spin ? 'apb-rotblink' : 'apb-blink';

  const eyes = faceless ? null :
  <>
      <RoundEye className={blinkClass} cx={e.cxL} cy={e.cy} r={e.r} fill={eyeFill} stroke={eyeStroke} sw={eyeSW} />
      <RoundEye className={blinkClass} cx={e.cxR} cy={e.cy} r={e.r} fill={eyeFill} stroke={eyeStroke} sw={eyeSW} />
    </>;


  // idle: eyes bob + glance. fly: just blink. patrol: eyes look ahead (toward
  // travel). spin: glance + blink during the pause between spins.
  const facePart = !animated ? eyes :
  fly ? eyes :
  spin ? <g className="apb-rotlook">{eyes}</g> :
  patrol ? <g className="apb-patrol-look">{eyes}</g> :
  <g className="apb-eyebob"><g className="apb-look">{eyes}</g></g>;

  const inner =
  <>
      <path d={rest} fill={bodyFill} stroke={bodyStroke} strokeWidth={strokeW} strokeLinejoin="round">
        {animated &&
      <animate attributeName="d" dur={morphDur} repeatCount="indefinite"
      values={morphVals} calcMode="spline" keyTimes="0;0.25;0.5;0.75;1"
      keySplines={`${SPL};${SPL};${SPL};${SPL}`} />
      }
      </path>
      {facePart}
    </>;


  // idle floats; fly banks; patrol banks while gliding; spin's rotation comes
  // from the wrapping GhostSpinner. The svg group itself just wobbles.
  const wrapped = !animated ? <g>{inner}</g> :
  fly ? <g className="apb-flytilt" style={{ transformBox: 'fill-box', transformOrigin: '50% 55%' }}>{inner}</g> :
  patrol ? <g className="apb-patrol-bank" style={{ transformBox: 'fill-box', transformOrigin: '50% 55%' }}>{inner}</g> :
  spin ? <g>{inner}</g> :
  <g className="apb-float">{inner}</g>;

  return (
    <svg viewBox="0 0 120 144" width={size} height={size * 1.2}
    style={{ overflow: 'visible', display: 'block', ...style }}>
      {defs && <defs>{defs}</defs>}
      {glow && !fly && !patrol && !spin &&
      <ellipse className={animated ? 'apb-shadow' : undefined} cx="60" cy="139" rx="33" ry="6"
      fill={AP.ink0} opacity="0.3" style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
      }
      {wrapped}
    </svg>);

}

// ── wordmark ───────────────────────────────────────────────────────────────
function Wordmark({ variant = 'lower', size = 40, tone = 'light', mono = null }) {
  const c = mono || (tone === 'light' ? AP.white : AP.ink);
  const accent = mono || AP.purpleSoft;
  const baseFont = { fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: size, letterSpacing: '-0.03em', lineHeight: 1, display: 'inline-flex', alignItems: 'baseline' };
  const monoFont = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: size * 0.92, letterSpacing: '-0.04em', lineHeight: 1, display: 'inline-flex' };

  if (variant === 'boo') return <span style={baseFont}><span style={{ color: c }}>api</span><span style={{ color: accent }}>boo</span></span>;
  if (variant === 'title') return <span style={{ ...baseFont, fontWeight: 700 }}><span style={{ color: c }}>Api</span><span style={{ color: accent }}>boo</span></span>;
  if (variant === 'mono') return <span style={{ ...monoFont, color: c }}>api<span style={{ color: accent }}>boo</span></span>;
  if (variant === 'replace') {
    return (
      <span style={{ ...baseFont, alignItems: 'center', gap: size * 0.03 }}>
        <span style={{ color: c }}>apib</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.04, transform: `translateY(${size * 0.05}px)` }}>
          <Ghost variant="outline" size={size * 0.95} />
          <Ghost variant="outline" size={size * 0.95} />
        </span>
      </span>);

  }
  return <span style={{ ...baseFont, color: c }}>apiboo</span>;
}

// ── app icon (squircle) ──────────────────────────────────────────────────
function AppIcon({ variant = 'classic', bg = 'black', size = 240, radius = null, animated = false }) {
  const r = radius != null ? radius : size * 0.225;
  let background,ring = 'rgba(255,255,255,0.06)';
  if (bg === 'black') background = `radial-gradient(120% 120% at 50% 0%, #16131f 0%, ${AP.ink0} 70%)`;else
  if (bg === 'gradient') {background = `linear-gradient(157deg, ${AP.purpleSoft} 0%, ${AP.purple} 45%, ${AP.purpleDeep} 100%)`;ring = 'rgba(255,255,255,0.20)';} else
  background = `radial-gradient(90% 90% at 50% 30%, #1d1830 0%, ${AP.ink} 60%, ${AP.ink0} 100%)`;

  const ghostMono = bg === 'gradient' ? '#fff' : null;
  const ghostVariant = bg === 'gradient' ? 'classic' : variant;

  return (
    <div style={{
      width: size, height: size, borderRadius: r, position: 'relative', overflow: 'hidden', background,
      boxShadow: `inset 0 1px 0 rgba(255,255,255,.12), inset 0 0 0 1px ${ring}, 0 ${size * 0.06}px ${size * 0.12}px -${size * 0.04}px rgba(0,0,0,.6)`,
      display: 'grid', placeItems: 'center'
    }}>
      {bg === 'glow' &&
      <div style={{ position: 'absolute', width: size * 0.82, height: size * 0.82, borderRadius: '50%',
        background: `radial-gradient(circle, ${AP.purple}55 0%, transparent 65%)`, filter: 'blur(3px)' }} />
      }
      {bg !== 'gradient' &&
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(70% 55% at 50% 20%, ${AP.purple}26, transparent 70%)` }} />
      }
      <div style={{ position: 'relative', transform: 'translateY(3%)' }}>
        <Ghost variant={ghostVariant} mono={ghostMono} size={size * 0.5} animated={animated} />
      </div>
    </div>);

}

// ── loader 1 · orbit (fly around a circle, comet trail) ────────────────────
function GhostLoader({ variant = 'classic', size = 36, speed = 1.5, trail = 4, box = null, style = {} }) {
  const B = box || Math.round(size * 2.5);
  const R = Math.max(6, B / 2 - size * 0.6);
  const n = trail + 1;
  const step = speed * 0.05;
  const flyers = [];
  for (let i = 0; i < n; i++) {
    const lead = i === 0;
    flyers.push(
      <div key={i} className="apb-flyer" style={{
        position: 'absolute', left: '50%', top: '50%',
        width: size, height: size * 1.2, marginLeft: -size / 2, marginTop: -size * 0.6,
        '--r': `${R}px`, animationDuration: `${speed}s`, animationDelay: `${-((n - i) * step)}s`,
        opacity: lead ? 1 : Math.max(0.1, 0.46 - (i - 1) * 0.12)
      }}>
        <div style={{ transform: lead ? 'none' : `scale(${1 - i * 0.1})`, transformOrigin: 'center' }}>
          <Ghost variant={variant} size={size} animated mode="fly" faceless={!lead} />
        </div>
      </div>
    );
  }
  flyers.reverse();
  return <div style={{ position: 'relative', width: B, height: B, display: 'inline-block', ...style }}>{flyers}</div>;
}

// ── loader 2 · patrol (smooth continuous flight side to side) ──────────────
function GhostPatrol({ variant = 'classic', size = 44, box = null, travel = null, style = {} }) {
  const B = box || Math.round(size * 3.1);
  const D = travel != null ? travel : Math.max(10, B / 2 - size * 0.62);
  return (
    <div style={{ position: 'relative', width: B, height: size * 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
      <div className="apb-patrol" style={{ '--d': `${D}px` }}>
        <div className="apb-patrol-rise">
          <Ghost variant={variant} size={size} animated mode="patrol" />
        </div>
      </div>
    </div>);

}

// ── loader 3 · rotate (spin fast → slow to a stop → look + blink → repeat) ──
function GhostSpinner({ variant = 'classic', size = 46, style = {} }) {
  return (
    <div style={{ display: 'inline-block', lineHeight: 0, ...style }}>
      <div className="apb-spin">
        <Ghost variant={variant} size={size} animated mode="spin" />
      </div>
    </div>);

}

export { Ghost, Wordmark, AppIcon, GhostLoader, GhostPatrol, GhostSpinner, AP };